/**
 * Lagna-wise functional benefic/malefic analysis from house ownership only (no placements).
 * Sign order: Mesha (0) … Meena (11).
 */
import { RASHI_CHART_ROWS, RASHI_CHART_ROW_ENGLISH } from './jyotishaRashiChart';
import { GRAHA_HI_EN_BY_EN } from './jyotishaGrahaNames';

/** English planet keys in classical lord order per rashi index */
export const SIGN_LORD_EN = [
  'Mars',
  'Venus',
  'Mercury',
  'Moon',
  'Sun',
  'Mercury',
  'Venus',
  'Mars',
  'Jupiter',
  'Saturn',
  'Saturn',
  'Jupiter',
];

const TRIKONA = new Set([1, 5, 9]);
const DUSTHANA = new Set([6, 8, 12]);
const MARAKA = new Set([2, 7]);

const SEVEN_KEYS = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn'];

/**
 * House numbers (1-12) owned by planet for this Lagna (whole-sign).
 * @param {number} lagnaIndex 0-11
 * @param {string} planetEn
 * @returns {number[]}
 */
export function housesOwnedByPlanet(lagnaIndex, planetEn) {
  const out = [];
  for (let h = 1; h <= 12; h++) {
    const signIdx = (lagnaIndex + h - 1) % 12;
    if (SIGN_LORD_EN[signIdx] === planetEn) out.push(h);
  }
  return out;
}

/** Natural benefics considered for Kendradhipati (naisargika). */
export const KENDRADHIPATI_BENEFICS = ['Moon', 'Mercury', 'Jupiter', 'Venus'];

const KENDRA_FOR_KENDRADHIPATI = [1, 4, 7, 10];
/** Maraka houses (2nd / 7th), death-inflicting lords in classical reading (dasha context). */
const MARAKA_HOUSES_FOR_BENEFIC = [2, 7];

/**
 * Kendradhipati + Maraka for natural benefics (whole-sign ownership only).
 * - Kendradhipati: owns ≥1 kendra (1,4,7,10) and does not own 5 or 9.
 * - Maraka lord: owns 2nd and/or 7th.
 * - Both: same planet has Kendradhipati pattern AND maraka lordship (e.g. 7th is kendra + maraka).
 */
export function getBeneficKendraMarakaAnalysis(lagnaIndex) {
  const rows = [];
  for (const planetEn of KENDRADHIPATI_BENEFICS) {
    const houses = housesOwnedByPlanet(lagnaIndex, planetEn);
    if (!houses.length) continue;
    const kendraHouses = houses.filter((h) => KENDRA_FOR_KENDRADHIPATI.includes(h));
    const marakaHouses = houses.filter((h) => MARAKA_HOUSES_FOR_BENEFIC.includes(h));
    const ownsTrikona59 = houses.some((h) => h === 5 || h === 9);
    const hasKendradhipati = kendraHouses.length > 0 && !ownsTrikona59;
    const hasMaraka = marakaHouses.length > 0;
    const both = hasKendradhipati && hasMaraka;
    if (hasKendradhipati || hasMaraka) {
      rows.push({
        planetEn,
        houses,
        kendraHouses,
        marakaHouses,
        hasKendradhipati,
        hasMaraka,
        both,
      });
    }
  }
  return {
    rows,
    presentKendradhipati: rows.some((r) => r.hasKendradhipati),
    presentMaraka: rows.some((r) => r.hasMaraka),
    presentBoth: rows.some((r) => r.both),
  };
}

/** @deprecated Use getBeneficKendraMarakaAnalysis; kept for compatibility. */
export function getKendradhipatiForLagna(lagnaIndex) {
  const { rows } = getBeneficKendraMarakaAnalysis(lagnaIndex);
  const affected = rows.filter((r) => r.hasKendradhipati).map((r) => ({
    planetEn: r.planetEn,
    houses: r.houses,
    kendraHouses: r.kendraHouses,
  }));
  return { present: affected.length > 0, affected };
}

/**
 * @param {number[]} houses
 * @returns {'Shubh'|'Ashubh'|'Mixed'|'Maraka'|'Neutral'}
 */
export function classifyFunctionalNature(houses) {
  if (!houses.length) return 'Neutral';

  const inT = houses.filter((h) => TRIKONA.has(h));
  const inD = houses.filter((h) => DUSTHANA.has(h));
  const inM = houses.filter((h) => MARAKA.has(h));

  if (inM.length === 2) return 'Maraka';
  if (inT.length && inD.length) return 'Mixed';
  if (inM.length && (inT.length || inD.length)) return 'Mixed';

  if (inD.length && !inT.length && !inM.length) return 'Ashubh';
  if (inT.length && !inD.length && !inM.length) return 'Shubh';
  if (inM.length === 1 && !inT.length && !inD.length) return 'Maraka';

  if (!inT.length && !inD.length && !inM.length) return 'Neutral';
  return 'Mixed';
}

/**
 * @param {number} lagnaIndex
 */
export function buildHouseRows(lagnaIndex) {
  const rows = [];
  for (let h = 1; h <= 12; h++) {
    const signIdx = (lagnaIndex + h - 1) % 12;
    const row = RASHI_CHART_ROWS[signIdx];
    rows.push({
      house: h,
      signIndex: signIdx,
      rashiHi: row.rashi,
      rashiNum: row.num,
      rashiSwamiHi: row.swami,
      signEn: RASHI_CHART_ROW_ENGLISH[signIdx],
      lordEn: SIGN_LORD_EN[signIdx],
      lordHi: grahaHi(SIGN_LORD_EN[signIdx]),
    });
  }
  return rows;
}

/**
 * @param {number} lagnaIndex
 * @returns {Array<{ planetEn: string, nature: string, houses: number[] }>}
 */
export function buildPlanetClassifications(lagnaIndex) {
  return SEVEN_KEYS.map((planetEn) => {
    const houses = housesOwnedByPlanet(lagnaIndex, planetEn);
    return {
      planetEn,
      nature: classifyFunctionalNature(houses),
      houses,
    };
  });
}

/** Latin-style rashi name for titles (Mesha … Meena) */
export const RASHI_LATIN_TITLE = [
  'Mesha',
  'Vrishabha',
  'Mithuna',
  'Karka',
  'Simha',
  'Kanya',
  'Tula',
  'Vrishchika',
  'Dhanu',
  'Makara',
  'Kumbha',
  'Meena',
];

/** 2-3 line lagnesh tendency (English + Hindi flavor) */
export const LAGNESH_INSIGHTS = [
  'Mars as Lagna lord: direct, competitive, physical courage; prefers honest effort and clear goals. लग्नेश मंगल, साहस और ऊर्जा, कभी जल्दबाज़ी।',
  'Venus as Lagna lord: harmony-seeking, aesthetic sense, relationship-aware; values comfort and steady pleasure. लग्नेश शुक्र, सौंदर्य और संतुलन की चाह।',
  'Mercury as Lagna lord: curious, communicative, adaptable; thrives on variety and skill. लग्नेश बुध, बुद्धि और लचीलापन।',
  'Moon as Lagna lord: receptive, mood-aware, nurturing; the inner screen changes with context. लग्नेश चंद्र, मन की लहरें।',
  'Sun as Lagna lord: visible self, dignity, leadership tone; wants clarity and respect. लग्नेश सूर्य, गरिमा और केंद्र।',
  'Mercury as Lagna lord: analytical, service-minded, detail-rich; mind refines through craft. लग्नेश बुध, विश्लेषण और सेवा।',
  'Venus as Lagna lord: diplomatic, partnership-oriented; balance and grace matter. लग्नेश शुक्र, मेल और सौहार्द।',
  'Mars as Lagna lord: intense, protective, strategic depth; courage with secrecy or research. लग्नेश मंगल, गहराई और रक्षा।',
  'Jupiter as Lagna lord: expansive, principled, teacher-flavoured; faith and meaning-seeking. लग्नेश गुरु, विस्तार और धर्म।',
  'Saturn as Lagna lord: disciplined, slow-built, duty-conscious; results through patience. लग्नेश शनि, धैर्य और कर्तव्य।',
  'Saturn as Lagna lord: systematic, network-aware, reform-minded; structure over hype. लग्नेश शनि, व्यवस्था और समय।',
  'Jupiter as Lagna lord: compassionate, imaginative, release-oriented; wisdom through surrender. लग्नेश गुरु, करुणा और मोक्ष-रंग।',
];

export const ADVANCED_GROUPS = [
  {
    key: 'upachaya',
    label: 'Upachaya',
    labelHi: 'उपचय',
    houses: [3, 6, 10, 11],
    hint: 'Growth through effort over time, houses where persistence compounds.',
  },
  {
    key: 'trishadaya',
    label: 'Trishadaya',
    labelHi: 'त्रिशदाय',
    houses: [3, 6, 11],
    hint: 'Often linked with exertion, rivalry, and gains - always read with the full chart.',
  },
  {
    key: 'panaphara',
    label: 'Panaphara',
    labelHi: 'पणफर',
    houses: [2, 5, 8, 11],
    hint: 'Succedent houses - continuation and resources between the angles.',
  },
  {
    key: 'apoklima',
    label: 'Apoklima',
    labelHi: 'अपोक्लिम',
    houses: [3, 6, 9, 12],
    hint: 'Cadent houses - learning, service, dharma, release (varies by school).',
  },
  {
    key: 'ayu',
    label: 'Ayu (longevity)',
    labelHi: 'आयु',
    houses: [8, 3],
    hint: 'Classically the 8th is primary for span of life; the 3rd is secondary in many texts - symbolic, not medical.',
  },
  {
    key: 'vyaya',
    label: 'Vyaya',
    labelHi: 'व्यय',
    houses: [12],
    hint: 'Loss, liberation, sleep, foreign lands - context decides the tone.',
  },
];

export const DISCLAIMER =
  'This analysis uses only lagna and house ownership (each planet’s functional role). It does not include actual placements. For a real reading, you need the full chart - positions, aspects, and dashas.';

export const DISCLAIMER_HI =
  'यह विश्लेषण केवल लग्न और भाव-स्वामित्व (ग्रहों की कार्यात्मक भूमिका) पर आधारित है। यहाँ असली ग्रह-स्थिति नहीं देखी जाती। सही पढ़ने के लिए पूरी कुंडली चाहिए - स्थिति, दृष्टि और दशा।';

export function grahaHi(planetEn) {
  return GRAHA_HI_EN_BY_EN[planetEn]?.hi ?? planetEn;
}

/** Split mixed
 * English prose + Hindi tail in LAGNESH_INSIGHTS entries.
 */
export function splitLagneshInsight(text) {
  const idx = text.search(/[\u0900-\u097F]/);
  if (idx <= 0) return { en: text.trim(), hi: text.trim() };
  return { en: text.slice(0, idx).trim(), hi: text.slice(idx).trim() };
}

/**
 * One-line friendly summary from classifications.
 * @param {Array<{ planetEn: string, nature: string }>} rows
 * @param {{ showEnglish?: boolean }} [opts]
 */
export function buildAutoSummary(rows, { showEnglish = true } = {}) {
  const shubh = rows.filter((r) => r.nature === 'Shubh').map((r) => (showEnglish ? r.planetEn : grahaHi(r.planetEn)));
  const ashubh = rows.filter((r) => r.nature === 'Ashubh').map((r) => (showEnglish ? r.planetEn : grahaHi(r.planetEn)));
  const mixed = rows.filter((r) => r.nature === 'Mixed').map((r) => (showEnglish ? r.planetEn : grahaHi(r.planetEn)));
  const maraka = rows.filter((r) => r.nature === 'Maraka').map((r) => (showEnglish ? r.planetEn : grahaHi(r.planetEn)));

  if (showEnglish) {
    const parts = [];
    if (shubh.length) parts.push(`growth-friendly tone from ${shubh.join(', ')}`);
    if (ashubh.length) parts.push(`possible friction from ${ashubh.join(', ')}`);
    if (mixed.length) parts.push(`mixed results through ${mixed.join(', ')}`);
    if (maraka.length) parts.push(`maraka emphasis for ${maraka.join(', ')} (contextual)`);
    if (!parts.length) return 'Ownership pattern is balanced between kendra and neutral lords, refine with full chart.';
    return `For this Lagna, ${parts.join('; ')}, always combine with placements and dasha.`;
  }

  const parts = [];
  if (shubh.length) parts.push(`${shubh.join(', ')} से विकास-अनुकूल रंग`);
  if (ashubh.length) parts.push(`${ashubh.join(', ')} से संभावित घर्षण`);
  if (mixed.length) parts.push(`${mixed.join(', ')} से मिश्रित फल`);
  if (maraka.length) parts.push(`${maraka.join(', ')} पर मारक बल (संदर्भानुसार)`);
  if (!parts.length) return 'स्वामित्व संतुलित है; पूर्ण कुण्डली से पुष्टि करें।';
  return `इस लग्न में ${parts.join('; ')}; स्थिति और दशा के साथ पढ़ें।`;
}
